import type {
  MedPatientBackground,
  MedPatientBackgroundUpdate,
  MedVisit,
  MedVisitBulkCopyRequest,
  MedVisitBulkDeleteRequest,
  MedVisitBulkUpdatePatch,
  MedVisitBulkUpdateRequest,
  MedVisitDetail,
} from "../types/medchron";
import type { APIInstance } from "./apiService";
import {
  type MedChronVersion,
  type MedChronVersionDuplicateResponse,
  MedChronVersionDuplicateResponseSchema,
  MedChronVersionSchema,
  type MedChronVersionUpdateRequest,
  type MedPatientBackgroundEditResponse,
  MedPatientBackgroundEditResponseSchema,
  type MedVisitBulkCopyResponse,
  MedVisitBulkCopyResponseSchema,
  type MedVisitBulkDeleteResponse,
  MedVisitBulkDeleteResponseSchema,
  type MedVisitBulkUpdateResponse,
  MedVisitBulkUpdateResponseSchema,
  MedVisitCitationSchema,
  type MedVisitCreateRequest,
  type MedVisitCreateResponse,
  MedVisitCreateResponseSchema,
  type MedVisitDeleteResponse,
  MedVisitDeleteResponseSchema,
  type MedVisitDetailDeleteResponse,
  MedVisitDetailDeleteResponseSchema,
  type MedVisitDetailEditResponse,
  MedVisitDetailEditResponseSchema,
  type MedVisitEditResponse,
  MedVisitEditResponseSchema,
  type MedVisitUpdateRequest,
} from "./medChronServiceV2.types";

const MedVisitCitationsSchema = MedVisitCitationSchema.array();

/**
 * Validates a visit's citations against the schema.
 * - Throws an error naming the visit when any citation is malformed, failing the whole response.
 */
const withParsedCitations = (visit: MedVisit): MedVisit => {
  const parsed = MedVisitCitationsSchema.safeParse(visit.citations);
  if (!parsed.success) {
    throw new Error(`Invalid citations for visit ${visit.visit_id}: ${parsed.error.message}`);
  }
  return { ...visit, citations: parsed.data };
};

export class MedChronServiceV2 {
  private api: APIInstance;

  constructor(api: APIInstance) {
    this.api = api;
  }

  async getVisits(caseId: string, versionId: string): Promise<MedVisit[]> {
    const response = await this.api.get<MedVisit[]>(
      `/cases/${caseId}/versions/${versionId}/visits`
    );
    return response.data.map(withParsedCitations);
  }

  async updateVisitDetail(
    caseId: string,
    versionId: string,
    visitId: string,
    oldCategory: string,
    detailData: MedVisitDetail
  ): Promise<MedVisitDetailEditResponse> {
    const response = await this.api.put(
      `/cases/${caseId}/versions/${versionId}/visits/${visitId}/details/${oldCategory}`,
      detailData
    );
    return MedVisitDetailEditResponseSchema.parse(response.data);
  }

  async createVisit(
    versionId: string,
    visitData: MedVisitCreateRequest
  ): Promise<MedVisitCreateResponse> {
    const response = await this.api.post(`/versions/${versionId}/visits`, visitData);
    return MedVisitCreateResponseSchema.parse(response.data);
  }

  async updateVisit(
    versionId: string,
    visitId: string,
    visitData: MedVisitUpdateRequest
  ): Promise<MedVisitEditResponse> {
    const response = await this.api.put(`/versions/${versionId}/visits/${visitId}`, visitData);
    return MedVisitEditResponseSchema.parse(response.data);
  }

  async deleteVisit(versionId: string, visitId: string): Promise<MedVisitDeleteResponse> {
    const response = await this.api.delete(`/versions/${versionId}/visits/${visitId}`);
    return MedVisitDeleteResponseSchema.parse(response.data);
  }

  async addVisitDetail(
    caseId: string,
    versionId: string,
    visitId: string,
    detailData: MedVisitDetail
  ): Promise<MedVisitDetailEditResponse> {
    const response = await this.api.post(
      `/cases/${caseId}/versions/${versionId}/visits/${visitId}/details`,
      detailData
    );
    return MedVisitDetailEditResponseSchema.parse(response.data);
  }

  async deleteVisitDetail(
    versionId: string,
    visitId: string,
    category: string
  ): Promise<MedVisitDetailDeleteResponse> {
    const response = await this.api.delete(
      `/versions/${versionId}/visits/${visitId}/details/${category}`
    );
    return MedVisitDetailDeleteResponseSchema.parse(response.data);
  }

  async getPatientBackground(caseId: string, versionId: string): Promise<MedPatientBackground> {
    const response = await this.api.get<MedPatientBackground>(
      `/cases/${caseId}/versions/${versionId}/patient-background`
    );
    return response.data;
  }

  async patchPatientBackground(
    caseId: string,
    versionId: string,
    data: MedPatientBackgroundUpdate
  ): Promise<MedPatientBackgroundEditResponse> {
    const response = await this.api.patch(
      `/cases/${caseId}/versions/${versionId}/patient-background`,
      data
    );
    return MedPatientBackgroundEditResponseSchema.parse(response.data);
  }

  async bulkUpdateVisits(
    caseId: string,
    versionId: string,
    visitIds: string[],
    patch: MedVisitBulkUpdatePatch
  ): Promise<MedVisitBulkUpdateResponse> {
    const body: MedVisitBulkUpdateRequest = { visit_ids: visitIds, patch };
    const response = await this.api.patch(
      `/cases/${caseId}/versions/${versionId}/visits/bulk`,
      body
    );
    return MedVisitBulkUpdateResponseSchema.parse(response.data);
  }

  async bulkDeleteVisits(
    caseId: string,
    versionId: string,
    visitIds: string[]
  ): Promise<MedVisitBulkDeleteResponse> {
    const body: MedVisitBulkDeleteRequest = { visit_ids: visitIds };
    const response = await this.api.post(
      `/cases/${caseId}/versions/${versionId}/visits/bulk-delete`,
      body
    );
    return MedVisitBulkDeleteResponseSchema.parse(response.data);
  }

  async bulkCopyVisits(
    targetVersionId: string,
    sourceVersionId: string,
    visitIds: string[]
  ): Promise<MedVisitBulkCopyResponse> {
    const body: MedVisitBulkCopyRequest = { visit_ids: visitIds };
    const response = await this.api.post(
      `/versions/${targetVersionId}/visits/copy-from/${sourceVersionId}`,
      body
    );
    return MedVisitBulkCopyResponseSchema.parse(response.data);
  }

  async renameVersion(versionId: string, versionName: string): Promise<MedChronVersion> {
    return this.updateVersion(versionId, { version_name: versionName });
  }

  async setCurrentVersion(versionId: string): Promise<MedChronVersion> {
    return this.updateVersion(versionId, { is_current: true });
  }

  async duplicateVersion(versionId: string): Promise<MedChronVersionDuplicateResponse> {
    const response = await this.api.post(`/versions/${versionId}/duplicate`);
    return MedChronVersionDuplicateResponseSchema.parse(response.data);
  }

  async deleteVersion(versionId: string): Promise<void> {
    await this.api.delete(`/versions/${versionId}`);
  }

  private async updateVersion(
    versionId: string,
    body: MedChronVersionUpdateRequest
  ): Promise<MedChronVersion> {
    const response = await this.api.patch(`/versions/${versionId}`, body);
    return MedChronVersionSchema.parse(response.data);
  }
}
